import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, Mail, ShieldCheck, FileText } from 'lucide-react';

const Machtigingsformulier = () => (
  <div>
    <section className="hero-gradient text-white py-14">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h1 className="text-4xl md:text-5xl font-bold uppercase">Machtigingsformulier</h1>
        <p className="text-slate-200 mt-2">Volmacht ten behoeve van Bezwaarmeesters.nl</p>
      </div>
    </section>

    <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 prose-content">
      <Card className="p-6 md:p-8 border border-slate-200 mb-8">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-[#f26b1f] flex-shrink-0 mt-1" />
          <div>
            <div className="font-bold text-[#1e1b4b] uppercase text-sm tracking-wide">Wat is een machtiging?</div>
            <p className="text-slate-700 mt-2 leading-relaxed">
              Met een machtiging (ook wel volmacht genoemd) geef je Bezwaarmeesters.nl schriftelijk toestemming om
              namens jou op te treden bij de bezwaar- en beroepsprocedure tegen jouw boete of beschikking.
              Zonder deze machtiging kunnen wij formeel niet namens jou communiceren met het CJIB, het Parket CVOM,
              gemeenten, waterschappen of andere bestuursorganen.
            </p>
          </div>
        </div>
      </Card>

      <h2>Wat houdt de machtiging in?</h2>
      <p>Door dit machtigingsformulier te ondertekenen bevestig je dat Bezwaarmeesters.nl bevoegd is om namens jou:</p>
      <ul>
        <li>Bezwaar of beroep in te dienen bij de bevoegde instantie (Officier van Justitie, gemeente, kantonrechter, gerechtshof).</li>
        <li>Correspondentie te voeren, processtukken op te stellen en in te dienen en alle relevante rechtsmiddelen aan te wenden.</li>
        <li>Stukken en informatie op te vragen bij het CJIB, Parket CVOM, gemeenten en andere bestuursorganen (waaronder flitsfoto's en kalibratiecertificaten).</li>
        <li>Hoorzittingen bij te wonen en jouw belangen daar te vertegenwoordigen.</li>
        <li>Namens jou de eventuele proceskostenvergoeding, dwangsommen en/of schadevergoedingen in ontvangst te nemen conform onze algemene voorwaarden.</li>
      </ul>

      <h2>Digitale ondertekening</h2>
      <p>
        Bij het invullen van het aanmeldformulier op onze website teken je digitaal met je muis, vinger of stylus.
        Deze digitale handtekening heeft dezelfde juridische waarde als een handtekening op papier
        (art. 3:15a Burgerlijk Wetboek). Wij bewaren de handtekening beveiligd bij jouw dossier en gebruiken
        deze uitsluitend voor de bezwaarprocedure.
      </p>

      <h2>Duur van de machtiging</h2>
      <p>
        De machtiging is geldig vanaf het moment van ondertekening en eindigt automatisch:
      </p>
      <ul>
        <li>bij afronding van de bezwaar- of beroepsprocedure (winst, verlies of intrekking); of</li>
        <li>op het moment dat je de machtiging schriftelijk intrekt via <a href="mailto:info@bezwaarmeesters.nl" className="text-[#f26b1f] underline">info@bezwaarmeesters.nl</a>.</li>
      </ul>
      <p>
        Let op: bij tussentijdse intrekking gelden de bepalingen uit onze <Link to="/algemene-voorwaarden" className="text-[#f26b1f] underline">algemene voorwaarden</Link>,
        waaronder mogelijk een vergoeding van gemaakte uren.
      </p>

      <h2>Wat als de boete niet op jouw naam staat?</h2>
      <p>
        Staat de boete op naam van een bedrijf of leasemaatschappij, dan is een aanvullende machtiging
        van de kentekenhouder vereist. Deze machtiging voeg je bij het aanmeldformulier toe als bestand.
        Dit is bijvoorbeeld het geval bij:
      </p>
      <ul>
        <li><strong>Bedrijfsauto's</strong>: machtiging van een tekenbevoegd persoon (bestuurder, HR of wagenparkbeheerder).</li>
        <li><strong>Leaseauto's</strong>: bevestiging van de leasemaatschappij dat jij als bestuurder gemachtigd bent om bezwaar in te dienen. Dit kan een leasecontract of een aparte machtiging zijn.</li>
      </ul>

      <h2>AVG en privacy</h2>
      <p>
        Alle persoonsgegevens die je in het kader van de machtiging verstrekt (NAW, contactgegevens, kenteken,
        handtekening, geuploade documenten) worden verwerkt conform ons <Link to="/privacybeleid" className="text-[#f26b1f] underline">privacybeleid</Link> en de Algemene Verordening Gegevensbescherming (AVG).
        Bezwaarmeesters.nl deelt geen gegevens met derden buiten wat strikt noodzakelijk is voor de behandeling van jouw zaak.
      </p>

      <div className="mt-10 p-6 rounded-lg bg-slate-50 border border-slate-200">
        <div className="flex items-start gap-3">
          <FileText className="w-6 h-6 text-[#1e1b4b] flex-shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-bold text-[#1e1b4b]">Klaar om te tekenen?</div>
            <p className="text-slate-700 mt-1 text-sm">Vul het aanmeldformulier in en teken de machtiging digitaal in de laatste stap. Duurt geen minuut.</p>
            <div className="mt-4">
              <Link to="/indienen">
                <Button className="bg-[#f26b1f] hover:bg-[#d4581a] text-white">Naar het aanmeldformulier <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-sm text-slate-600 flex flex-wrap gap-4">
        <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#f26b1f]" /> <a href="mailto:info@bezwaarmeesters.nl" className="hover:text-[#1e1b4b]">info@bezwaarmeesters.nl</a></div>
      </div>
    </article>
  </div>
);

export default Machtigingsformulier;
